import { useEffect, useRef } from 'react';
import { TextLayer } from 'pdfjs-dist';
import '../styles/pdfTextLayer.css';

/**
 * PdfTextLayer — pdf.js text layer (transparent glyphs over the canvas) so
 * document text is selectable/copyable. Mounted per page; virtualization
 * keeps it to visible±1 pages. `interactive` gates pointer-events so the
 * annotation/edit tools above keep working when a tool is active.
 */
export default function PdfTextLayer({ pdfDoc, pageNum, scale, interactive = true }) {
  const containerRef = useRef(null);
  const layerRef = useRef(null);

  useEffect(() => {
    if (!pdfDoc || !containerRef.current) return;
    let cancelled = false;
    const container = containerRef.current;

    (async () => {
      try {
        const page = await pdfDoc.getPage(pageNum);
        if (cancelled) return;
        const viewport = page.getViewport({ scale });
        while (container.firstChild) container.removeChild(container.firstChild);
        container.style.setProperty('--scale-factor', String(scale));
        container.style.width = `${viewport.width}px`;
        container.style.height = `${viewport.height}px`;
        const layer = new TextLayer({
          textContentSource: page.streamTextContent(),
          container,
          viewport,
        });
        layerRef.current = layer;
        await layer.render();
      } catch (err) {
        // Cancelled renders and per-page text failures are non-fatal — the
        // page simply isn't selectable; never break the viewer.
        if (import.meta.env?.DEV && !cancelled) console.warn('TextLayer render failed', pageNum, err);
      }
    })();

    return () => {
      cancelled = true;
      layerRef.current?.cancel?.();
      layerRef.current = null;
      while (container.firstChild) container.removeChild(container.firstChild);
    };
  }, [pdfDoc, pageNum, scale]);

  return (
    <div
      ref={containerRef}
      className="textLayer"
      // z-index 0 (not higher): EditLayer/AnnotationLayer roots are z-auto,
      // and any explicit positive z-index here would paint above them and
      // steal their clicks. DOM order keeps us under the interactive
      // layers. `.textLayer` in pdfTextLayer.css already declares
      // `z-index:0` (pdf.js's own stock value) — no inline override needed.
      style={{ position: 'absolute', top: 0, left: 0, pointerEvents: interactive ? 'auto' : 'none' }}
    />
  );
}
